/**
 * SAT percentile ranks: nationally representative and user group.
 *
 * Source: College Board Research, "SAT Nationally Representative and User Percentiles",
 * https://research.collegeboard.org/reports/sat-suite/understanding-scores/sat. Retrieved
 * 2026-10-16 (parsed from the page's two HTML tables: 121 total-score rows and 61 section-score
 * rows; the server reported the page last modified 2026-10-10). The page carries no data-year
 * label. It states:
 * - "A student's percentile rank represents the percentage of students with scores equal to or
 *   lower than their score."
 * - "Nationally representative percentiles are derived from a research study of U.S. students
 *   in 11th and 12th grade and are weighted to represent all U.S. students in those grades,
 *   regardless of whether they typically take the SAT."
 * - "User group percentiles are based on the actual SAT scores of students that graduated in
 *   the past three school years."
 *
 * Cells are stored exactly as College Board prints them, including "99+" at the top and "1-" at
 * the bottom of the scale.
 */

export const SAT_TOTAL_MIN = 400;
export const SAT_TOTAL_MAX = 1600;
export const SAT_SECTION_MIN = 200;
export const SAT_SECTION_MAX = 800;

export type SatGroup = "nationally" | "user";
export type SatSectionName = "readingWriting" | "math";

/** [total, nationally representative, user group], 1600 down to 400. */
const TOTAL: readonly (readonly [number, string, string])[] = [
  [1600, "99+", "99+"],
  [1590, "99+", "99+"],
  [1580, "99+", "99+"],
  [1570, "99+", "99+"],
  [1560, "99+", "99+"],
  [1550, "99+", "99"],
  [1540, "99+", "99"],
  [1530, "99+", "99"],
  [1520, "99+", "98"],
  [1510, "99", "98"],
  [1500, "99", "97"],
  [1490, "99", "97"],
  [1480, "99", "97"],
  [1470, "99", "96"],
  [1460, "99", "96"],
  [1450, "99", "95"],
  [1440, "98", "95"],
  [1430, "98", "94"],
  [1420, "98", "94"],
  [1410, "97", "93"],
  [1400, "97", "93"],
  [1390, "97", "92"],
  [1380, "96", "91"],
  [1370, "96", "91"],
  [1360, "95", "90"],
  [1350, "94", "89"],
  [1340, "94", "88"],
  [1330, "93", "88"],
  [1320, "93", "87"],
  [1310, "92", "86"],
  [1300, "91", "85"],
  [1290, "90", "84"],
  [1280, "89", "83"],
  [1270, "88", "82"],
  [1260, "87", "81"],
  [1250, "86", "80"],
  [1240, "85", "79"],
  [1230, "84", "78"],
  [1220, "83", "77"],
  [1210, "82", "76"],
  [1200, "81", "75"],
  [1190, "80", "74"],
  [1180, "78", "72"],
  [1170, "77", "71"],
  [1160, "76", "70"],
  [1150, "74", "69"],
  [1140, "73", "67"],
  [1130, "71", "66"],
  [1120, "70", "65"],
  [1110, "69", "63"],
  [1100, "67", "62"],
  [1090, "65", "60"],
  [1080, "63", "59"],
  [1070, "61", "58"],
  [1060, "60", "56"],
  [1050, "58", "55"],
  [1040, "56", "53"],
  [1030, "54", "52"],
  [1020, "52", "50"],
  [1010, "50", "48"],
  [1000, "48", "47"],
  [990, "46", "45"],
  [980, "44", "44"],
  [970, "42", "42"],
  [960, "40", "41"],
  [950, "38", "39"],
  [940, "36", "38"],
  [930, "35", "36"],
  [920, "33", "35"],
  [910, "31", "33"],
  [900, "29", "32"],
  [890, "27", "30"],
  [880, "26", "29"],
  [870, "24", "27"],
  [860, "23", "26"],
  [850, "21", "24"],
  [840, "20", "23"],
  [830, "18", "21"],
  [820, "17", "20"],
  [810, "16", "18"],
  [800, "14", "17"],
  [790, "13", "16"],
  [780, "11", "14"],
  [770, "10", "13"],
  [760, "9", "12"],
  [750, "8", "11"],
  [740, "7", "10"],
  [730, "6", "9"],
  [720, "5", "8"],
  [710, "4", "7"],
  [700, "4", "6"],
  [690, "3", "5"],
  [680, "2", "5"],
  [670, "2", "4"],
  [660, "1", "4"],
  [650, "1", "3"],
  [640, "1", "3"],
  [630, "1", "3"],
  [620, "1-", "2"],
  [610, "1-", "2"],
  [600, "1-", "2"],
  [590, "1-", "2"],
  [580, "1-", "2"],
  [570, "1-", "1"],
  [560, "1-", "1"],
  [550, "1-", "1"],
  [540, "1-", "1"],
  [530, "1-", "1"],
  [520, "1-", "1"],
  [510, "1-", "1"],
  [500, "1-", "1"],
  [490, "1-", "1-"],
  [480, "1-", "1-"],
  [470, "1-", "1-"],
  [460, "1-", "1-"],
  [450, "1-", "1-"],
  [440, "1-", "1-"],
  [430, "1-", "1-"],
  [420, "1-", "1-"],
  [410, "1-", "1-"],
  [400, "1-", "1-"],
];

/** [score, R&W nationally, R&W user, Math nationally, Math user], 800 down to 200. */
const SECTION: readonly (readonly [number, string, string, string, string])[] =
  [
    [800, "99+", "99+", "99+", "99+"],
    [790, "99+", "99+", "99+", "99"],
    [780, "99+", "99+", "99", "98"],
    [770, "99+", "99", "99", "97"],
    [760, "99+", "99", "99", "96"],
    [750, "99", "98", "98", "95"],
    [740, "99", "97", "98", "95"],
    [730, "99", "96", "97", "94"],
    [720, "98", "95", "97", "93"],
    [710, "97", "94", "96", "92"],
    [700, "97", "92", "95", "91"],
    [690, "96", "91", "94", "90"],
    [680, "95", "89", "93", "88"],
    [670, "93", "88", "92", "87"],
    [660, "92", "86", "91", "85"],
    [650, "90", "84", "90", "84"],
    [640, "88", "82", "89", "82"],
    [630, "86", "80", "87", "81"],
    [620, "84", "77", "85", "79"],
    [610, "81", "75", "83", "77"],
    [600, "79", "72", "81", "75"],
    [590, "76", "70", "79", "73"],
    [580, "74", "67", "76", "71"],
    [570, "71", "65", "73", "69"],
    [560, "68", "62", "71", "66"],
    [550, "65", "59", "68", "64"],
    [540, "62", "56", "65", "61"],
    [530, "58", "53", "61", "59"],
    [520, "55", "50", "57", "56"],
    [510, "51", "47", "52", "53"],
    [500, "48", "43", "47", "50"],
    [490, "44", "40", "44", "48"],
    [480, "41", "37", "40", "45"],
    [470, "38", "34", "36", "42"],
    [460, "34", "32", "32", "40"],
    [450, "31", "29", "29", "37"],
    [440, "28", "26", "25", "34"],
    [430, "24", "24", "23", "31"],
    [420, "22", "21", "20", "28"],
    [410, "19", "19", "17", "24"],
    [400, "16", "16", "15", "21"],
    [390, "13", "14", "13", "18"],
    [380, "11", "11", "10", "15"],
    [370, "9", "9", "9", "12"],
    [360, "7", "8", "7", "10"],
    [350, "5", "6", "5", "8"],
    [340, "3", "5", "4", "6"],
    [330, "2", "4", "3", "5"],
    [320, "2", "3", "2", "4"],
    [310, "1", "2", "1", "3"],
    [300, "1", "2", "1", "2"],
    [290, "1-", "2", "1-", "2"],
    [280, "1-", "1", "1-", "2"],
    [270, "1-", "1", "1-", "1"],
    [260, "1-", "1", "1-", "1"],
    [250, "1-", "1", "1-", "1"],
    [240, "1-", "1", "1-", "1"],
    [230, "1-", "1", "1-", "1"],
    [220, "1-", "1", "1-", "1"],
    [210, "1-", "1", "1-", "1"],
    [200, "1-", "1-", "1-", "1-"],
  ];

export interface SatPercentile {
  /** The cell as College Board prints it, such as "81", "99+" or "1-". */
  label: string;
  /** The number in the cell; 99 for "99+" and 1 for "1-". */
  value: number;
  /** "plus" for 99+, "minus" for 1-, otherwise null. */
  qualifier: "plus" | "minus" | null;
}

export function parsePercentileCell(cell: string): SatPercentile {
  if (cell.endsWith("+")) {
    return { label: cell, value: Number.parseInt(cell, 10), qualifier: "plus" };
  }
  if (cell.endsWith("-")) {
    return {
      label: cell,
      value: Number.parseInt(cell, 10),
      qualifier: "minus",
    };
  }
  return { label: cell, value: Number.parseInt(cell, 10), qualifier: null };
}

export interface SatTotalRow {
  score: number;
  nationally: SatPercentile;
  user: SatPercentile;
}

export interface SatSectionRow {
  score: number;
  readingWriting: { nationally: SatPercentile; user: SatPercentile };
  math: { nationally: SatPercentile; user: SatPercentile };
}

export const SAT_TOTAL_ROWS: readonly SatTotalRow[] = TOTAL.map(
  ([score, nationally, user]) => ({
    score,
    nationally: parsePercentileCell(nationally),
    user: parsePercentileCell(user),
  })
);

export const SAT_SECTION_ROWS: readonly SatSectionRow[] = SECTION.map(
  ([score, rwNationally, rwUser, mathNationally, mathUser]) => ({
    score,
    readingWriting: {
      nationally: parsePercentileCell(rwNationally),
      user: parsePercentileCell(rwUser),
    },
    math: {
      nationally: parsePercentileCell(mathNationally),
      user: parsePercentileCell(mathUser),
    },
  })
);

function isStep10(
  value: number | null,
  min: number,
  max: number
): value is number {
  return (
    value !== null &&
    Number.isInteger(value) &&
    value >= min &&
    value <= max &&
    value % 10 === 0
  );
}

export function isSatTotal(value: number | null): value is number {
  return isStep10(value, SAT_TOTAL_MIN, SAT_TOTAL_MAX);
}

export function isSatSection(value: number | null): value is number {
  return isStep10(value, SAT_SECTION_MIN, SAT_SECTION_MAX);
}

export function satTotalRow(score: number): SatTotalRow | null {
  return SAT_TOTAL_ROWS.find((row) => row.score === score) ?? null;
}

export function satSectionRow(score: number): SatSectionRow | null {
  return SAT_SECTION_ROWS.find((row) => row.score === score) ?? null;
}

/** The lowest total whose percentile value is at least `target` in the group. */
export function satLowestTotalForPercentile(
  group: SatGroup,
  target: number
): number | null {
  let best: number | null = null;
  for (const row of SAT_TOTAL_ROWS) {
    const cell = row[group];
    const reaches = cell.qualifier === "minus" ? false : cell.value >= target;
    if (reaches && (best === null || row.score < best)) {
      best = row.score;
    }
  }
  return best;
}
